import { GreyBox } from '@dealstack/frontend/shared/components/common/GreyBox';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { AddressInput } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/AddressInput';
import { PivBasicInformationInput as PivBasicInformation } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/PivBasicInformationInput';
import { RepresentativePersonalInformationInput } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/RepresentativePersonalInformationInput';
import { Flex, Title } from '@mantine/core';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { AuthorisedSignatorySection } from './AuthorisedSignatorySection';
import { ControllingPartiesSection } from './ControllingPartiesSection';
import {
	JOIN_ALLOCATION_PIV_INFO_FORM,
	useJoinAndAllocationPIVInformationController,
} from './useJoinAndAllocationPIVInformationController';

const JoinAndAllocationPIVInformation = () => {
	const tInvestor = useTranslationInvestor();

	const { pivInformationForm, isControllingPartyRequired, investorPersonId } =
		useJoinAndAllocationPIVInformationController();

	const disablePivInputs = !pivInformationForm.watch(JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV);
	const disableRepresentativeFields = pivInformationForm.watch(
		JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
	);

	return (
		<Flex direction="column" gap="2rem">
			<FormProvider {...pivInformationForm}>
				<GreyBox>
					<Title order={5} mb="0.5rem">
						{tInvestor('investment.piv.question')}
					</Title>

					<AdvancedSwitch
						twoWay={true}
						selectedValue={pivInformationForm.watch(
							JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV
						)}
						leftText={tInvestor('investment.piv.personally')}
						rightText={tInvestor('investment.piv.company')}
						onChange={() => {
							pivInformationForm.setValue(
								JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV,
								!pivInformationForm.watch(JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV),
								{ shouldDirty: true }
							);
						}}
						dataTestId={
							INVESTOR_TEST_ELEMENT_ID.INVESTOR_PERSONAL_INFORMATION_IS_PIV_CHECKBOX
						}
					/>
				</GreyBox>

				<PivInputs disabled={disablePivInputs}>
					<PivBasicInformation
						pivNameFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.PIV_NAME}
						pivRegNumberFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.PIV_REG_NUMBER}
						disabled={disablePivInputs}
					/>

					<AddressInput
						countryFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.PIV_COUNTRY}
						cityFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.PIV_CITY}
						postCodeFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.PIV_POST_CODE}
						streetFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.PIV_STREET}
						houseNumberFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.PIV_HOUSE_NUMBER}
						disabled={disablePivInputs}
						title={tInvestor('investment.piv.address')}
						dataTestId={INVESTOR_TEST_ELEMENT_ID.INVESTOR_PIV_ADDRESS_INPUTS}
					/>
				</PivInputs>

				{isControllingPartyRequired ? (
					<Flex direction="column" gap="2rem">
						{!disablePivInputs && <ControllingPartiesSection />}
						<SignatorySectionWrapper disabled={disablePivInputs}>
							<AuthorisedSignatorySection
								investorPersonId={investorPersonId}
								disabled={disablePivInputs}
							/>
						</SignatorySectionWrapper>
					</Flex>
				) : (
					<>
						<GreyBox disabled={disablePivInputs}>
							<Title order={5} mb="0.5rem">
								{tInvestor('investment.piv.representative_question.question')}
							</Title>

							<AdvancedSwitch
								twoWay={true}
								selectedValue={pivInformationForm.watch(
									JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
								)}
								leftValue={true}
								rightValue={false}
								leftText={tInvestor('investment.piv.representative_question.yes')}
								rightText={tInvestor('investment.piv.representative_question.no')}
								disabledWithMiddleState={disablePivInputs}
								onChange={() => {
									pivInformationForm.setValue(
										JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR,
										!pivInformationForm.watch(
											JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
										),
										{ shouldDirty: true }
									);
								}}
								dataTestId={
									INVESTOR_TEST_ELEMENT_ID.INVESTOR_PERSONAL_INFORMATION_IS_REPRESENTATIVE_SAME_AS_INVESTOR
								}
							/>
						</GreyBox>

						<RepresentativeInputsWrapper disabled={disablePivInputs}>
							<RepresentativePersonalInformationInput
								firstNameFieldName={
									JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME
								}
								lastNameFieldName={
									JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME
								}
								emailFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_EMAIL}
								disabled={disableRepresentativeFields}
							/>
						</RepresentativeInputsWrapper>
					</>
				)}
			</FormProvider>
		</Flex>
	);
};

const PivInputs = styled.div<{ disabled?: boolean }>`
	display: flex;
	gap: 5rem;
	${({ disabled }) =>
		disabled &&
		css`
			opacity: 32%;
		`};
`;

const RepresentativeInputsWrapper = styled.div<{ disabled?: boolean }>`
	display: flex;
	gap: 5rem;
	${({ disabled }) =>
		disabled &&
		css`
			opacity: 32%;
		`};
`;

const SignatorySectionWrapper = styled.div<{ disabled?: boolean }>`
	${({ disabled }) =>
		disabled &&
		css`
			opacity: 32%;
		`};
`;
const _JoinAndAllocationPIVInformationExport = memo(JoinAndAllocationPIVInformation);

export { _JoinAndAllocationPIVInformationExport as JoinAndAllocationPIVInformation };
